<template>
  <div class="battle-report-wrapper">
    <h1>Battle Report</h1>
    <h2>{{ report?.outcomeQualifier }} {{ report?.winner === 0 ? "victory" : "defeat" }}</h2>
    <div v-if="report">
      <div class="meta-info">
        <div>
          <div class="summary">
            <p>Report ID: {{ id }}</p>
            <p>Date: {{ formattedDate }}</p>
            <p>
              <strong>Winner:</strong> {{ report.winner === 0 ? "Attacker" : "Defender" }}
            </p>
            
            <p>
              <strong>Target: <span :style="{ color: targetColor(report)}"> {{ targetName(report) }}</span></strong>
            </p>
            <p>
              <strong>Hero Leading Attack:</strong> {{ report.attackerHero.name }}
              {{ getTraitGlyph(report.attackerHero.trait) }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="report" class="participants">
        <div class="participant attacker">
          <div class="big">Attacker</div>
          <TroopsInfo :combatGroups="report.turns[0].attacker" />
          <HeroInfo :hero="report.attackerHero" v-if="report.attackerHero" />
        </div>

        <div class="participant defender">
          <div class="big">Defender</div>
          <TroopsInfo :combatGroups="report.turns[0].defender" />
          <HeroInfo :hero="report.attackerHero" v-if="report.defenderHero" />
        </div>
      </div>

      <div class="turns" v-if="report.turns && report.turns.length > 0">
        <div class="big">Battle Turns</div>
        <div
          v-for="turn in report.turns.slice(0, report.turns.length - 1)"
          :key="turn.turnNumber"
          class="turn"
        >
          <strong>Turn {{ turn.turnNumber }}</strong>
          
          
          <div class="targeting-row">
          <div class="side-targeting">
            <template v-if="turnTargetingMap(turn.turnNumber).random">
              <div class="side-label-attacker">random targets</div>
            </template>
            <template v-else>
              <div class="side-label-attacker">attacker targets:</div>
              <div  class="side-label-glyph"
                v-for="(rule, i) in turnTargetingMap(turn.turnNumber).attacker"
                :key="'a' + i"
              >
                <div>
                  <span class="trait">{{ getTraitGlyph(rule.from) }}</span>
                  <span class="arrow"> → </span>
                  <span class="targets">{{ rule.to.map(getTraitGlyph).join(', ') }}</span>
                </div>
              </div>
            </template>
          </div>

          <div class="side-targeting">
            <template v-if="turnTargetingMap(turn.turnNumber).random">
              <div class="side-label-defender">random targets</div>
            </template>
            <template v-else>
              <div class="side-label-defender">defender targets:</div>
              <div class="side-label-glyph"
                v-for="(rule, i) in turnTargetingMap(turn.turnNumber).defender"
                :key="'d' + i"
              >
                <div>
                  <span class="trait">{{ getTraitGlyph(rule.from) }}</span>
                  <span class="arrow"> → </span>
                  <span class="targets">{{ rule.to.map(getTraitGlyph).join(', ') }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
          
          
          <div class="sides">
            <div>
              <div>Attacker</div>
              <ul>
                <li v-for="troop in turn.attacker" :key="troop.name">
                  {{ troop.count }} {{ troop.name }} {{ getTraitGlyph(troop.trait) }}
                </li>
              </ul>
            </div>
            <div>
              <div>Defender</div>
              <ul>
                <li v-for="troop in turn.defender" :key="troop.name">
                  {{ troop.count }} {{ troop.name }} {{ getTraitGlyph(troop.trait) }}
                </li>
              </ul>
            </div>
          </div>
          <div class="sub-turns">
            <div v-for="(subTurn, index) in turn.subTurnResult" :key="index">
              <div>
                <span class="sub-turn-title"
                  >sub turn {{ index + 1 }}
                  {{ subTurn.attackingSide === 0 ? "attacker" : "defender" }}:</span
                >
                {{ subTurnTraits(subTurn) }}
              </div>
              <ul>
                <li v-for="casualty in subTurn.defenderCasualties" :key="casualty.name">
                  <span class="troop-name">{{ casualty?.name }}:</span>
                  {{ subTurnCasualties(subTurn, casualty.troopId) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="result">
        <div>
          <span class="big">Battle Result:</span>
          {{ report.winner === 0 ? "attacker" : "defender" }} wins
        </div>

        <div v-if="report" class="participants">
          <div class="participant attacker">
            <div class="big">Attacker casualties</div>
            <div>
              <TroopsInfo :combatGroups="totalCasualties(report).attackerCasualties" />
            </div>
          </div>

         

          <div class="participant defender">
            <div class="big">Defender casualties</div>
            <div>
              <TroopsInfo :combatGroups="totalCasualties(report).defenderCasualties" />
            </div>
          </div>

          <div class="participant drops" v-if="report.drops">
            <div class="big">Drops</div>
            <div>
              <DropsList :drops="report.drops" />
            </div>
          </div>

          <div class="participant xp" v-if="report.drops">
            <div class="big">XP gained</div>
            <div>
              <span class="side-label-attacker">{{ report.attackerHero.name }}:</span> +{{ report.attackerHero.xpGained }} XP 
            </div>
            <div v-if="report.defenderHero">
              <span class="side-label-defender">{{ report.defenderHero?.name }}:</span> +{{ report.defenderHero?.xpGained }} XP 
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import HeroInfo from "../components/battle-report/HeroInfo.vue";
import TroopsInfo from "../components/battle-report/TroopsInfo.vue";
import { getTraitGlyph } from "../game/Helpers.ts";
import { IBattleReport, ISubTurnResult } from "../game/shared/IBattleReport.ts";
import { TargetType } from "../game/shared/IMarchingArmy.ts";
import { ICombatGroup } from "../game/shared/IMarchingArmy.ts";
import { difficultyColors, difficultyNames } from "../game/logic/Globals.ts";

import DropsList from "../components/battle-report/DropsList.vue";

export default defineComponent({
  components: {
    HeroInfo,
    TroopsInfo,
    DropsList,
    getTraitGlyph,
  },  
  data() {
    return {
      id: this.$route.params.id,
      report: null as IBattleReport | null,
      difficultyColors,
    };
  },
  computed: {
    formattedDate(): string {
      if (!this.report || !this.report.date) return "";
      const date = new Date(this.report.date);
      return date.toLocaleString();
    },
  },
  methods: {
    turnTargetingMap(turnNumber: number) {
      // enum mapping must match your shared model: 0=footman, 1=mounted, 2=ranged
      const F = 0, M = 1, R = 2;
      const map = {
        random: false,
        attacker: [] as { from: number; to: number[] }[],
        defender: [] as { from: number; to: number[] }[],
      };

      switch (turnNumber) {
        case 1:
          // attacker: R -> R, F, M | defender: R -> M, R, F
          map.attacker.push({ from: R, to: [R, F, M] });
          map.defender.push({ from: R, to: [M, R, F] });
          break;

        case 2:
          // attacker: R -> F, M, R; M -> F, M, R
          map.attacker.push({ from: R, to: [F, M, R] });
          map.attacker.push({ from: M, to: [F, M, R] });
          // defender: R -> M, R, F; F -> M, F
          map.defender.push({ from: R, to: [M, R, F] });
          map.defender.push({ from: F, to: [M, F] });
          break;

        case 3:
          // attacker: R -> F, R, M; M -> F, M, R; F -> M, F, R
          map.attacker.push({ from: R, to: [F, R, M] });
          map.attacker.push({ from: M, to: [F, M, R] });
          map.attacker.push({ from: F, to: [M, F, R] });
          // defender: R -> F, R, M; F -> M, F, R; M -> R, M, F
          map.defender.push({ from: R, to: [F, R, M] });
          map.defender.push({ from: F, to: [M, F, R] });
          map.defender.push({ from: M, to: [R, M, F] });
          break;

        default:
          map.random = true; // Turn 4+: random targets
          break;
      }
      return map;
    },    
    getTraitGlyph,
    targetColor(report: IBattleReport) {
      return `#${difficultyColors[!!report.targetDifficulty ? report.targetDifficulty - 1 : 0].toString(16)}`;
    },
    targetName(report: IBattleReport) {
      if (
        report.targetType == TargetType.EventLocation ||
        report.targetType == TargetType.InstancedLocation
      ) {
        return `${report.targetName} [${difficultyNames[!!report.targetDifficulty ? report.targetDifficulty - 1 : 0]}]`;
      }
      return report.targetName;
    },
    unitsCount(groups: ICombatGroup[]) {
      return groups.reduce((acc, troop) => acc + troop.count, 0);
    },
    unitNames(groups: ICombatGroup[]) {
      const names =
        groups.length > 0
          ? groups.map((troop) => `${troop.count} ${troop.name}`).join(", ")
          : "0 units";
      return names;
    },
    subTurnTraits(subTurn: ISubTurnResult) {
      return `${getTraitGlyph(subTurn.attackerTrait)} ${this.unitNames(
        subTurn.attacker
      )} [${(subTurn.traitBonus * 100).toFixed(2)}% attack] -> ${getTraitGlyph(
        subTurn.defenderTrait
      )}  ${this.unitNames(subTurn.defender)}`;
    },
    subTurnCasualties(subTurn: ISubTurnResult, troopId: number) {
      const defender = subTurn.defender.find((x) => x.troopId == troopId);
      const casualty = subTurn.defenderCasualties.find((x) => x.troopId == troopId);
      return `${casualty?.lost}/${defender?.count} lost`;
    },
    totalCasualties(report: IBattleReport) {
      // total casualties = first turn combat group counts - last turn combat group counts
      const attackCasualties: ICombatGroup[] = report.turns[0].attacker.map((troop) => {
        const lastTurnTroop = report.turns[report.turns.length - 1].attacker.find(
          (x) => x.troopId == troop.troopId
        );
        return { ...troop, count: troop.count - (lastTurnTroop?.count || 0) };
      });
      const defenseCasualties: ICombatGroup[] = report.turns[0].defender.map((troop) => {
        const lastTurnTroop = report.turns[report.turns.length - 1].defender.find(
          (x) => x.troopId == troop.troopId
        );
        return { ...troop, count: troop.count - (lastTurnTroop?.count || 0) };
      });

      return {
        attackerCasualties: attackCasualties,
        defenderCasualties: defenseCasualties,
      };
    },
  },
  async mounted() {
    try {
      let url = `${import.meta.env.VITE_WEB_SOCKET_URL}/battle-report-data/${this.id}`;
      url = url.replace("wss:", "https:").replace("ws:", "http:");
      console.log(`fetching report data from: ${url}`);
      const response = await fetch(url);
      if (response.ok) {
        this.report = await response.json();
      } else {
        console.error("Failed to fetch battle report:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching battle report:", error);
    }
  },
});
</script>

<style scoped>

h1 {
  margin-bottom: 0;
  text-align: center;
  font-size: 2.5em; 
  color: #e0c097; /* Lighter color for the title */
}
h2 {
  text-align: center;
  margin: 10px 0 20px;
  font-size: 1.6em;
  color: #e0c097;
}


.battle-report-wrapper {
  height: 100vh;
  width: 100vw;
  overflow-y: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: #999;
  color: rgb(47, 37, 5);
  font-family: "Courier New", Courier, monospace;
  font-size: 1.1em;
}

.big {
  font-size: 1.2em;
  font-weight: bold;
  margin: 15px 0;
  display: inline-block;
}

.meta-info {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0 0 30px;
}
.summary {
  display: flex;
  flex-direction: column;
  p {
    margin: 2px;
  }
}

.participants {
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: center;
}

.participant {
  padding: 20px;
  border: 1px solid #e0c097; /* Use a subtle border color from global scheme */
  border-radius: 5px;
}

.turns {
  margin-bottom: 40px;
}

.turn {
  border: 1px solid #e0c097;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 5px;
}

.side-targeting {
  margin: 5px 10px 20px;
  font-size: 1.2rem;
}
.side-label-attacker {
  color: #ba3939;
}

.side-label-glyph {
  display: inline-block;
  margin: 5px 10px;
}
  .side-label-glyph .arrow {
    color: rgb(255, 24, 24);
    font-size: 1.8rem;
  }
  .side-label-glyph .trait {
    background-color: rgb(147, 127, 127);
    padding: 8px;
  }
.side-label-defender {
  color: #2e51ed;
}

.troop-name {
  display: inline-block;
  width: 250px;
  text-align: right;
}

.sub-turn-title {
  display: inline-block;
  font-weight: bold;
  width: 305px;
  margin-top: 15px;
}

ul {
  list-style-type: none;
  margin: 5px 0 10px 10px;
}
</style>
